import React from 'react';
import { v4 } from 'uuid';
import { UseOpenWorkspacePanelWorkspaceConfig } from '../panels/use-open-workspace-panel';
import { useIntegratedActionBroadcaster } from '../action-broadcaster/use-integrated-action-broadcaster';
import { CadNextAction } from '../action-broadcaster/types';

export type OpenEventPanelArgs = {
    eventId?: number;
    dontFocusWindow?: boolean;
    shouldFocusPanel?: boolean;
    workspacePanelConfig?: Omit<
        UseOpenWorkspacePanelWorkspaceConfig<'EVENT'>,
        'workspacePanelType'
    >;
    // these activeTab and agencyEventNumber APIs are here to support opening the event panel to specific tabs
    // in the legacy cad event window - they can be removed when the legacy cad event
    // window is no longer in use
    activeTab?: 'LOCATION_CAUTIONS' | 'UNIT_RECOMMENDATIONS';
    agencyEventNumber?: string;
};

export type OpenEventPanel = (args: OpenEventPanelArgs) => void;

export type OpenScheduledEventPanelArgs = {
    eventId?: number;
    shouldFocus?: boolean;
    workspacePanelConfig?: Partial<UseOpenWorkspacePanelWorkspaceConfig<'SCHEDULED_EVENT'>>;
};

export function useOpenEventPanel(): (args: OpenEventPanelArgs) => void {
    const { trigger } = useIntegratedActionBroadcaster();

    return React.useCallback(
        (args: OpenEventPanelArgs) => {
            const initialTab = args.workspacePanelConfig?.panelState?.initialTab;
            const eventId = args.eventId;
            trigger([
                {
                    id: v4(),
                    targetWindowType: 'ROOT',
                    type: 'OPEN_EVENT_V2_WINDOW',
                    payload: {
                        dontFocus: args.dontFocusWindow,
                    },
                },
                {
                    id: v4(),
                    targetWindowType: 'EVENT_V2',
                    type: 'OPEN_EVENT_PANEL',
                    payload: args,
                },
                // It's not often that we set the `initialTab`
                // when opening up an event.  However, when we do,
                // and the event is already open, it will not activate
                // the `initialTab`.  Since in most cases, we do want
                // this tab to be set even when the event is already open,
                // we forcefully trigger this event to set it
                ...(eventId && initialTab
                    ? [
                          {
                              id: v4(),
                              targetWindowType: 'EVENT_V2',
                              type: 'SET_EVENT_PANEL_TAB',
                              payload: {
                                  eventId,
                                  tab: initialTab,
                              },
                          } as CadNextAction,
                      ]
                    : []),
            ]);
        },
        [trigger]
    );
}

export function useOpenScheduledEventPanel(): (args: OpenScheduledEventPanelArgs) => void {
    const { trigger } = useIntegratedActionBroadcaster();

    return React.useCallback(
        (args: OpenScheduledEventPanelArgs) => {
            trigger([
                {
                    id: v4(),
                    targetWindowType: 'ROOT',
                    type: 'OPEN_EVENT_V2_WINDOW',
                },
                {
                    id: v4(),
                    targetWindowType: 'EVENT_V2',
                    type: 'OPEN_SCHEDULED_EVENT_PANEL',
                    payload: args,
                },
            ]);
        },
        [trigger]
    );
}
